// The whole web page of a project's app (ADR 0021): its own sign-in page, its app bar and account menu,
// the project's apps (the app view), the full page of every pinned companion, and on every project page
// the companions' overlays (the chat window, the tours), the speed dial and the chart windows. A project's
// web/main.tsx renders <AppShell companions={…}/> with the `./app` entries of the SDKs it pins.
import { ConfirmProvider, Toaster } from '@cortex/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useParams,
  type RouteObject,
} from 'react-router';
import { applyBranding } from './branding.ts';
import { AppView } from './AppView.tsx';
import { AppAuthProvider, RequireSignIn, SignedOutOnly } from './auth.tsx';
import { ChartWindows } from './ChartWindows.tsx';
import { CompanionDial, type DialHost } from './CompanionDial.tsx';
import { CompanionPagesProvider, useCompanionPages, type AppCompanion } from './companions.tsx';
import { parseProjectPath, projectPath } from './paths.ts';
import { appInfo, appJson, moduleRuns } from './session.ts';
import { AppSignIn, signInSettled } from './SignIn.tsx';

/** The page's query cache: the SDK views share it (a chat tool that changed data marks it stale). */
export const appQueryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false, staleTime: 0 } },
});

function home() {
  const i = appInfo();
  const base = `/p/${encodeURIComponent(i.project.slug)}/b/${encodeURIComponent(i.branch)}`;
  const first = i.apps[0];
  return first ? `${base}/apps/${encodeURIComponent(first.key)}` : `${base}/apps`;
}

const Home = () => <Navigate to={home()} replace />;

function RouteError() {
  return (
    <div className="p-6 text-sm text-page-muted" data-testid="route-error">
      This page could not be shown.
    </div>
  );
}

function Layout({ companions }: { companions: AppCompanion[] }) {
  const { slug = null, branch = null } = useParams();
  const location = useLocation();
  const rest = parseProjectPath(location.pathname).rest;
  const openApp = /^apps\/([^/]+)$/.exec(rest ?? '')?.[1] ?? null;
  const app = openApp ? decodeURIComponent(openApp) : null;
  React.useEffect(signInSettled, []);
  const pages = useCompanionPages();
  // The dial (the same component as cortex's Apps page) on this app's backend: the companions its
  // declaration names, narrowed to the SDKs it pins, and their pages in this shell.
  const host = React.useMemo<DialHost>(
    () => ({
      companions: async (s, b, a) =>
        (
          (
            await appJson<{ companions?: { key: string; label: string; page: string | null }[] }>(
              `/api/structure/projects/${encodeURIComponent(s)}/branches/${encodeURIComponent(b)}/apps/${encodeURIComponent(a)}/companions`,
            )
          )?.companions ?? []
        ).filter((c) => moduleRuns(c.key)),
      pages,
      pagePath: (page, s, b) => projectPath(page, s, b),
    }),
    [pages],
  );
  const dial = React.useCallback(
    (p: { slug: string; branch: string; app: string; hidden: boolean }) => (
      <CompanionDial host={host} slug={p.slug} branch={p.branch} app={p.app} hidden={p.hidden} />
    ),
    [host],
  );
  const dialOwned = companions.some((c) => c.ownsDial && c.Overlay);
  return (
    <>
      <div className="h-screen flex w-full overflow-hidden bg-page-bg" data-testid="standalone-shell">
        {/* No bar: the app (its frame) or an SDK page, full-bleed, as in cortex's preview. */}
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="flex-1 overflow-auto" data-testid="page" data-tour="page">
            <Outlet />
          </div>
        </main>
      </div>
      {slug &&
        companions.map((c) =>
          c.Overlay ? (
            <c.Overlay
              key={`${c.module}:${slug}`}
              slug={slug}
              branch={branch ?? 'main'}
              app={app}
              dial={dial}
            />
          ) : null,
        )}
      {slug && app && !dialOwned && (
        <CompanionDial host={host} slug={slug} branch={branch ?? 'main'} app={app} />
      )}
      <ChartWindows />
    </>
  );
}

export function appRoutes(companions: AppCompanion[]): RouteObject[] {
  const pinned = companions.filter((c) => moduleRuns(c.module));
  const pages = Object.assign({}, ...pinned.map((c) => c.pages ?? {})) as Record<
    string,
    () => Promise<React.ComponentType>
  >;
  return [
    {
      element: <SignedOutOnly />,
      children: [{ path: '/login', element: <AppSignIn /> }],
    },
    {
      path: '/',
      element: <RequireSignIn />,
      errorElement: <RouteError />,
      children: [
        { index: true, element: <Home /> },
        {
          path: 'p/:slug/b/:branch',
          element: (
            <CompanionPagesProvider pages={pages}>
              <Layout companions={pinned} />
            </CompanionPagesProvider>
          ),
          children: [
            { index: true, element: <Home /> },
            { path: 'apps', element: <Home /> },
            { path: 'apps/:viewName', element: <AppView />, errorElement: <RouteError /> },
            ...Object.entries(pages).map(([path, load]): RouteObject => ({
              path,
              lazy: async () => ({ Component: await load() }),
              hydrateFallbackElement: <div className="min-h-full bg-page-bg" />,
              errorElement: <RouteError />,
            })),
            { path: '*', element: <Home /> },
          ],
        },
        { path: '*', element: <Home /> },
      ],
    },
  ];
}

export function AppShell({ companions }: { companions: AppCompanion[] }) {
  const router = React.useMemo(() => createBrowserRouter(appRoutes(companions)), [companions]);
  return (
    <QueryClientProvider client={appQueryClient}>
      <AppAuthProvider>
        <ConfirmProvider>
          <RouterProvider router={router} />
          <Toaster />
        </ConfirmProvider>
      </AppAuthProvider>
    </QueryClientProvider>
  );
}

/** Renders the app's page into #root, in the app's own colour from the first paint. */
export async function mountApp(companions: AppCompanion[]) {
  applyBranding();
  const { createRoot } = await import('react-dom/client');
  createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <AppShell companions={companions} />
    </React.StrictMode>,
  );
}
